import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { PageHeader, SimpleSelect } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UsersRound, Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const ROLE_OPTIONS = [
  { label: "Super Admin", value: "super_admin", desc: "Akses penuh." },
  { label: "Admin", value: "admin", desc: "Operasional, tanpa hapus permanen." },
  { label: "Admin SIPORT", value: "admin_siport", desc: "Lihat saja: Dashboard & Data Laporan SIPORT." },
  { label: "Kepala Unit", value: "kepala_unit", desc: "Lihat saja: 5 menu Indikator Mutu + Dashboard & Data Laporan SIPORT, tanpa export." },
];

const emptyForm = { username: "", nama: "", role: "admin", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [list, setList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const superAdminCount = list.filter((u) => u.role === "super_admin").length;

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ username: u.username, nama: u.nama || "", role: u.role || "admin", password: "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editId && (!form.username.trim() || !form.password)) return toast.error("Username dan password wajib diisi");
    if (form.password && form.password.length < 6) return toast.error("Password minimal 6 karakter");
    setSaving(true);
    try {
      if (editId) {
        const body = { nama: form.nama, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${editId}`, body);
        toast.success("Akun diperbarui");
      } else {
        await api.post("/users", {
          username: form.username.trim(), nama: form.nama.trim(), role: form.role, password: form.password,
        });
        toast.success("Akun dibuat");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Akun dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-page">
      <PageHeader icon={UsersRound} title="Kelola Akun User" subtitle="Buat dan atur akun Admin. Hanya Super Admin yang bisa membuka halaman ini.">
        <Button onClick={openAdd} data-testid="user-add-button">
          <Plus className="w-4 h-4 mr-2" /> Tambah Akun
        </Button>
      </PageHeader>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Daftar Akun ({list.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nama</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((u) => {
                  const isSelf = u.id === me?.id;
                  const isLastSuper = u.role === "super_admin" && superAdminCount <= 1;
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        {u.nama || "—"}
                        {isSelf && <Badge variant="secondary" className="ml-2">Anda</Badge>}
                      </TableCell>
                      <TableCell className="text-slate-600">@{u.username}</TableCell>
                      <TableCell>
                        <Badge className={u.role === "super_admin" ? "bg-brand-sky/30 text-brand-deep" : "bg-slate-100 text-slate-700"}>
                          {u.role === "super_admin" && <ShieldCheck className="w-3 h-3 mr-1" />}
                          {ROLE_LABEL[u.role] || "Admin"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        {!isSelf && !isLastSuper && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`}>
                                <Trash2 className="w-4 h-4 text-rose-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus akun @{u.username}?</AlertDialogTitle>
                                <AlertDialogDescription>Akun ini tidak akan bisa masuk lagi. Data mutu tidak terpengaruh.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del(u.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-user-${u.id}`}>Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {list.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Belum ada akun</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="user-dialog" className="w-[calc(100vw-2rem)] max-w-lg overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Akun" : "Tambah Akun"}</DialogTitle>
            <DialogDescription>
              {editId ? "Kosongkan password jika tidak ingin mengubahnya." : "Password minimal 6 karakter."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 w-full max-w-full min-w-0">
            <div className="space-y-1 min-w-0">
              <Label>Username</Label>
              <Input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="mis. pjmutu.iprjt"
                disabled={!!editId}
                className="w-full max-w-full"
                data-testid="user-username-input"
              />
              {editId && <p className="text-xs text-slate-400">Username tidak bisa diubah.</p>}
            </div>
            <div className="space-y-1 min-w-0">
              <Label>Nama</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="mis. PJ Mutu IPRJT" className="w-full max-w-full" data-testid="user-nama-input" />
            </div>
            <div className="space-y-1 min-w-0">
              <Label>Role</Label>
              <SimpleSelect testId="user-role-select" value={form.role} onChange={(v) => setForm({ ...form, role: v })}
                options={ROLE_OPTIONS} className="w-full max-w-full" />
              <p className="text-xs text-slate-500 break-words whitespace-normal" data-testid="user-role-desc">
                {ROLE_OPTIONS.find((o) => o.value === form.role)?.desc}
              </p>
            </div>
            <div className="space-y-1 min-w-0">
              <Label>{editId ? "Password Baru (opsional)" : "Password"}</Label>
              <Input type="password" value={form.password} autoComplete="new-password" className="w-full max-w-full"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editId ? "Biarkan kosong = tidak diubah" : "Minimal 6 karakter"}
                data-testid="user-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} data-testid="user-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
